import { rd, RemoteData } from '@passionware/monads';
import { Resource } from '__types__';
import { ArrowUpRight, Check, Plus } from 'lucide-react';
import { useState } from 'react';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import type { ResourceMembership } from 'v5/core/entity-schema/entitySchema.types';
import { Skeleton } from 'v5/design-system/Skeleton';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import {
  EntityCreateAction,
  EntityCreateDialogContent,
  EntityCreateDialogShell,
  EntityCreateShellContext
} from 'v5/patterns/forms/dialogs/EntityCreateDialog';
import { CreateDraftSummary } from './CreateDraftSummary';
import { ResourceCreateForm } from './ResourceCreateForm';
import { ResourceDuplicatesFooterConnected } from './ResourceDuplicatesFooter.connected';
import { CreateDraft, ResourceFormServices } from './useResourceFormModel';

export type ResourceCreatedNext = 'close' | 'add-another' | 'go-to-profile';

export interface ResourceCreateDialogProps extends ResourceFormServices {
  open: boolean;
  onOpenChange(open: boolean): void;
  /** `null` → the first resource type available in the browsing scope. */
  initialResourceTypeId: number | null;
  initialMembership: ResourceMembership;
  /** Side effects only (toast, navigation); the dialog closes or resets itself. */
  onCreated(resource: Resource, next: ResourceCreatedNext): void;
}

const ACTIONS: readonly EntityCreateAction<ResourceCreatedNext>[] = [
  {
    id: 'add-another',
    label: 'Save & add another',
    icon: <Plus />,
    after: 'reset'
  },
  {
    id: 'go-to-profile',
    label: 'Save & go to profile',
    icon: <ArrowUpRight />,
    after: 'close'
  },
  {
    id: 'close',
    label: 'Save',
    icon: <Check />,
    primary: true,
    after: 'close'
  }
];

/**
 * Create-resource dialog on the generic {@link EntityCreateDialogShell}:
 * step 1 picks the resource type and visibility, step 2 is the schema form
 * with the recap pinned under the title.
 */
export function ResourceCreateDialog({
  open,
  onOpenChange,
  initialResourceTypeId,
  initialMembership,
  onCreated,
  services
}: ResourceCreateDialogProps) {
  return (
    <EntityCreateDialogShell open={open} onOpenChange={onOpenChange}>
      {shell => (
        <ResourceCreateDialogContent
          initialResourceTypeId={initialResourceTypeId}
          initialMembership={initialMembership}
          onCreated={onCreated}
          services={services}
          {...shell}
        />
      )}
    </EntityCreateDialogShell>
  );
}

function ResourceCreateDialogContent({
  initialResourceTypeId,
  initialMembership,
  onCreated,
  services,
  onDirtyChange,
  close
}: Omit<ResourceCreateDialogProps, 'open' | 'onOpenChange'> &
  EntityCreateShellContext) {
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const identity = rd.tryMap(scopeRd, appScopeUtils.identityFromResolved);
  const organizationId = rd.tryMap(scopeRd, scope => scope.organizationId);
  const resourceTypesRd =
    services.resourceTypeService.useResourceTypes(identity);
  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId,
    'active'
  );
  const resourceTypeIdRd = rd.map(resourceTypesRd, types =>
    initialResourceTypeId !== null
      ? initialResourceTypeId
      : (types[0]?.id ?? null)
  );

  // Step-1 choices live here: the form edits them and the pinned recap bar
  // shows them. Until the user touches them they derive from the props.
  const [draftEdits, setDraftEdits] = useState<CreateDraft | null>(null);
  const draftRd: RemoteData<CreateDraft | null> = rd.map(
    resourceTypeIdRd,
    resourceTypeId =>
      draftEdits ??
      (resourceTypeId === null
        ? null
        : {
            resourceTypeId,
            profilePicture: null,
            membership: initialMembership
          })
  );
  const draft = rd.mapOrElse(draftRd, value => value, null);
  // Typed in the form, shown by the duplicate warning in the footer.
  const [typedName, setTypedName] = useState('');

  return (
    <EntityCreateDialogContent<Resource, ResourceCreatedNext>
      title={{
        setup: 'Create resource · type & visibility',
        form: 'Create resource · details'
      }}
      description="Create a new resource"
      canProceed={
        // "Specific workspaces" with none picked would create it hidden.
        draft !== null &&
        !(
          draft.membership.type === 'workspaces' &&
          draft.membership.workspaceIds.length === 0
        )
      }
      actions={ACTIONS}
      onCreated={(created, next) => {
        // The next resource keeps type and visibility, not this one's face.
        if (next === 'add-another') {
          setDraftEdits(edits => edits && { ...edits, profilePicture: null });
          setTypedName('');
        }
        onCreated(created, next);
      }}
      errorFallback="Failed to create resource"
      onDirtyChange={onDirtyChange}
      close={close}
      renderSummary={({ onEdit, canEdit }) =>
        draft && (
          <CreateDraftSummary
            draft={draft}
            resourceTypes={resourceTypesRd}
            workspaces={workspacesRd}
            onEdit={onEdit}
            editDisabled={!canEdit}
          />
        )
      }
      renderFooterNotice={({ formKey }) =>
        rd.tryMap(scopeRd, scope => (
          <ResourceDuplicatesFooterConnected
            key={formKey}
            typedName={typedName}
            organizationId={scope.organizationId}
            browsingWorkspaceId={
              scope.level === 'organization' ? null : scope.workspaceId
            }
            workspaces={rd.tryGet(workspacesRd)}
            services={services}
          />
        ))
      }
      renderForm={({ step, formKey, formRef, onStateChange, requestSubmit }) =>
        // The body is not padded (the form spans it); these pad themselves.
        rd
          .journey(draftRd)
          .wait(
            <div className="px-6 py-3">
              <Skeleton className="h-64 w-full" />
            </div>
          )
          .catch(error => <div className="px-6 py-3">{renderError(error)}</div>)
          .map(draft =>
            draft === null ? (
              <p className="px-6 py-3 text-fg-muted">
                No resource types are available.
              </p>
            ) : (
              <ResourceCreateForm
                key={formKey}
                ref={formRef}
                step={step}
                draft={draft}
                onDraftChange={update =>
                  setDraftEdits(edits => update(edits ?? draft))
                }
                onStateChange={onStateChange}
                onSubmitRequest={requestSubmit}
                onNameChange={setTypedName}
                services={services}
              />
            )
          )
      }
    />
  );
}
